import { memo, useMemo, useRef } from 'react';
import {
  CartesianGrid,
  Label,
  Line,
  LineChart as RechartsLineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useSelector } from 'react-redux';
import { extractModalStateFromAnalysis } from '../../../redux/selectors/analyses';
import useIncidents from '../hooks/useIncidents';
import {
  useAxesProps,
  useChartLines,
  useFormattedChartData,
  useYAxisWidth,
} from '../hooks';
import CustomTooltip from '../CustomTooltip';
import CustomXAxisTick from '../CustomXAxisTick';
import CustomYAxisTick from '../CustomYAxisTick';
import CustomLineDot from '../CustomLineDot';
import CustomReferenceDotLabel from '../CustomReferenceDotLabel';
import {
  CHART_MARGIN,
  DATA_KEY,
  DEFAULT_X_AXIS_CONFIG,
  getXAxisPadding,
} from '../constants/utils';
import { useYAxisBounds } from '../hooks';
import { isNil } from 'lodash';
import { assertCondition } from '../../../utils/types';
import { DateKindKey, QueryType } from '../../../utils/apollo/graphqlSchema';
import { bootstrapTimezoneSelector } from '../../../redux/selectors/bootstrap';
import { useAppSelector } from '../../../redux/hooks';
import { YAxisPosition } from '../interfaces';
import ComposedAxisLabel from '../ComposedAxisLabel';
import ResponsiveChartWrapper from '../ResponsiveChartWrapper';
import useBuilderInfoDisplay from '../utils/useBuilderInfoDisplay';
import { useOptimizedChartData } from '../../../utils/hooks/useOptimizedChartData';
import { computeMeasureValue } from '../../../utils/formatting';
import { ChartMode } from '../types';
import useSingleChartDataTooltip from '../hooks/tooltip/useSingleChartDataTooltip';
import SingleChartDataTooltip from '../SingleChartDataTooltip';
import usePrepareCohort from '../hooks/usePrepareCohort';
import { useTooltipWheelContext } from '../CustomTooltip/CustomTooltipWheelContext';
import {
  CHART_OPACITY_ACTIVE,
  CHART_OPACITY_INACTIVE,
} from '../hooks/useBarChartOpacity';
import { IChartLineType } from '../hooks/useChartLines';
import useCacheParsedChartLlmRawDataEffect from '../hooks/llmCacheRawData/useCacheParsedChartLlmRawDataEffect';
import { useFloatingLayerContext } from '../../FloatingManager';
import { theme } from '../../../styles/theme';
import PopperPortal from '../CustomTooltip/PopperPortal';
import { useChartContext } from '../ChartContext';

interface IProps {
  analysisId: string;
  mode: ChartMode;
}

const lineStrokeDashArrayLookup: Record<
  IChartLineType,
  CSSStyleDeclaration['strokeDasharray']
> = {
  normal: '1 0',
  dashed: '7 5',
  pattern: '2 2',
};

export default memo(function LineChart({ analysisId, mode }: IProps) {
  const { tooltipContentRef } = useTooltipWheelContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const modalsStateMemoizedPropSelector = useMemo(
    extractModalStateFromAnalysis,
    [],
  );
  /* Chart modals state */
  const isCreateIncidentModalOpen = useSelector((state) =>
    modalsStateMemoizedPropSelector(
      state,
      analysisId,
      'isCreateIncidentModalOpen',
    ),
  );
  const createIncidentData = useSelector((state) =>
    modalsStateMemoizedPropSelector(state, analysisId, 'createIncidentData'),
  );

  const orgTimezone = useAppSelector(bootstrapTimezoneSelector);
  const {
    chartData,
    settings: { chartSettings, chartWidth, incidents },
    displayedMeasures,
    displayedSlices,
    metadata: { isBreakdownOverall, query },
    colorConfig,
  } = useChartContext();
  assertCondition(query?.__typename === 'ExpressionQuery');
  const { activeIncidentTypes } = chartSettings;

  const { dateKind } = query;
  const { chartType, axisSettings } = chartSettings;

  // narrow down chartData to IParsedChartDataExpression
  assertCondition(chartData.queryType === QueryType.Expression);
  const { data, linesMeta, dataKeysOnMeasure } = chartData;

  const chartDates = useMemo(
    () => data?.map((singleDataPoint: any) => singleDataPoint.date as string),
    [data],
  );

  const {
    tooltipProps,
    isTooltipLocked,
    onLineDotMouseEnter,
    lockTooltipPositionHandler,
    onMouseLeave,
    clearLockedTooltipProps,
  } = useSingleChartDataTooltip({
    containerRef,
  });

  const { handleCreateCohortFromExpression } = usePrepareCohort({
    analysisId,
  });

  const { getYAxisDomain } = useYAxisBounds();

  const { visibleLines, displayedHighlights } = useChartLines({
    chartSettings,
    chartData,
    isBreakdownOverall,
    colorConfig,
  });

  const { leftAxisProps, rightAxisProps, getMeasureYAxisPositionByKey } =
    useAxesProps();

  const hasLeftAxis = !isNil(leftAxisProps);
  const hasRightAxis = !isNil(rightAxisProps);
  const isDisplayMeasureLabel = displayedMeasures.length > 1;
  const yDefaultAxisId = hasLeftAxis
    ? YAxisPosition.LEFT
    : hasRightAxis
      ? YAxisPosition.RIGHT
      : undefined;

  const { renderIncidents, renderCreateIncidentReferenceLine } = useIncidents({
    analysisId,
    incidents,
    createIncidentData,
    isCreateIncidentModalOpen,
    chartDates,
    activeIncidentTypes,
    yAxisId: yDefaultAxisId,
  });

  const builderInfoDisplay = useBuilderInfoDisplay(analysisId);
  const formattedData = useFormattedChartData({
    chartData: data,
    displayedMeasures,
    dataKeysOnMeasure,
  });
  const { xAxisSeries, yAxisDataKeyCreator, xAxisKey } = useOptimizedChartData(
    formattedData,
    DATA_KEY,
  );

  const tooltipZIndex = useFloatingLayerContext();

  // XXX also cache the raw chart numbers in redux, to be used as a context sent to the LLM
  useCacheParsedChartLlmRawDataEffect({
    chartTypeName: 'Line',
    parsedChartDataRecords: formattedData,
    analysisId,
  });

  // Calculate the Y axis width dynamically
  const yAxisWidth = useYAxisWidth({
    label:
      leftAxisProps && isDisplayMeasureLabel ? leftAxisProps.measureName : '',
  });

  return (
    <>
      <ResponsiveChartWrapper analysisId={analysisId} mode={mode}>
        <ResponsiveContainer ref={containerRef}>
          <RechartsLineChart
            data={xAxisSeries}
            margin={CHART_MARGIN}
            // edge case: when the dot is cut by the border of chart, mouseLeaveOnDot will not be triggered
            onMouseLeave={onMouseLeave}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey={xAxisKey}
              {...DEFAULT_X_AXIS_CONFIG}
              tick={(props) => (
                <CustomXAxisTick {...props} shouldRotate={false} />
              )}
              tickMargin={10}
              padding={getXAxisPadding({ hasLeftAxis, hasRightAxis })}
            />
            {hasLeftAxis && (
              <YAxis
                padding={{ top: 16, bottom: 0 }}
                yAxisId={YAxisPosition.LEFT}
                orientation="left"
                hide={!axisSettings.yAxisSettings[YAxisPosition.LEFT].isVisible}
                tickSize={0}
                domain={getYAxisDomain({
                  yAxisPosition: YAxisPosition.LEFT,
                  visibleLines,
                })}
                allowDataOverflow={true}
                axisLine={false} // Whether to display the Y axis vertical line or not
                tick={
                  <CustomYAxisTick
                    measureDisplayFormat={leftAxisProps.format}
                  />
                }
                tickCount={5}
                tickMargin={10}
                width={yAxisWidth}
              >
                {isDisplayMeasureLabel && (
                  <Label
                    value={leftAxisProps.measureName}
                    position="left"
                    fontWeight={500}
                    angle={-90}
                    content={(props) => (
                      <ComposedAxisLabel
                        props={props}
                        chartWidth={chartWidth}
                        displayedMeasures={displayedMeasures.filter(
                          (measure) =>
                            measure.yAxisPosition === YAxisPosition.LEFT &&
                            measure.isVisible,
                        )}
                      />
                    )}
                  />
                )}
              </YAxis>
            )}
            {hasRightAxis && (
              <YAxis
                padding={{ top: 16, bottom: 0 }}
                yAxisId={YAxisPosition.RIGHT}
                orientation="right"
                hide={
                  !axisSettings.yAxisSettings[YAxisPosition.RIGHT].isVisible
                }
                tickSize={0}
                domain={getYAxisDomain({
                  yAxisPosition: YAxisPosition.RIGHT,
                  visibleLines,
                })}
                axisLine={false} // Wether to display the Y axis vertical line or not
                allowDataOverflow={true}
                tick={
                  <CustomYAxisTick
                    measureDisplayFormat={rightAxisProps.format}
                  />
                }
                tickCount={5}
                tickMargin={10}
                // need to handle the case when there is no left axis
                width={hasLeftAxis ? yAxisWidth : yAxisWidth + 32}
              >
                {isDisplayMeasureLabel && (
                  <Label
                    value={rightAxisProps.measureName}
                    position="right"
                    fontWeight={500}
                    angle={90}
                    content={(props) => (
                      <ComposedAxisLabel
                        props={props}
                        chartWidth={chartWidth}
                        displayedMeasures={displayedMeasures.filter(
                          (measure) =>
                            measure.yAxisPosition === YAxisPosition.RIGHT &&
                            measure.isVisible,
                        )}
                      />
                    )}
                  />
                )}
              </YAxis>
            )}
            <Tooltip
              offset={20}
              wrapperStyle={{
                zIndex: tooltipZIndex,
                position: mode === ChartMode.Compact ? 'fixed' : 'absolute',
              }}
              allowEscapeViewBox={{ x: false, y: true }}
              content={(props: any) =>
                isNil(tooltipProps) ? (
                  <PopperPortal active={props.active}>
                    {CustomTooltip({
                      props,
                      lines: visibleLines,
                      chartType,
                      chartValues: formattedData,
                      linesMeta,
                      timezone: orgTimezone,
                      displayedMeasures,
                      displayedSlices,
                      additionalLabel: builderInfoDisplay,
                      colorConfig,
                      isBreakdownOverall,
                      tooltipPayloadContentRef: tooltipContentRef,
                    })}
                  </PopperPortal>
                ) : null
              }
            />
            {renderIncidents}
            {renderCreateIncidentReferenceLine}
            {visibleLines?.map((line, index) => {
              const yAxisId = getMeasureYAxisPositionByKey(line.measureKey);
              const lineOpacity =
                !tooltipProps || tooltipProps?.line?.name === line.name
                  ? CHART_OPACITY_ACTIVE
                  : CHART_OPACITY_INACTIVE;
              return (
                <Line
                  connectNulls={false}
                  isAnimationActive={false}
                  key={`line-${index}`}
                  name={line.name}
                  dataKey={yAxisDataKeyCreator(line.name)}
                  stroke={line.color}
                  opacity={lineOpacity}
                  strokeDasharray={lineStrokeDashArrayLookup[line.type]}
                  strokeWidth={2}
                  yAxisId={yAxisId}
                  dot={(props) => {
                    return (
                      <CustomLineDot
                        {...props}
                        line={line}
                        data={data}
                        chartType={chartType}
                        opacity={lineOpacity}
                        active={
                          isTooltipLocked &&
                          tooltipProps?.line?.name === line.name &&
                          tooltipProps?.date === props.payload[xAxisKey]
                        }
                      />
                    );
                  }}
                  activeDot={(props: any) => {
                    return (
                      <CustomLineDot
                        {...props}
                        line={line}
                        onDotClick={lockTooltipPositionHandler}
                        onMouseOver={() => {
                          onLineDotMouseEnter(
                            {
                              x: props.cx,
                              y: props.cy,
                              date: props.payload[xAxisKey],
                              xAxisKey,
                            },
                            line,
                          );
                        }}
                        onMouseLeave={onMouseLeave}
                        chartType={chartType}
                        opacity={lineOpacity}
                      />
                    );
                  }}
                />
              );
            })}

            {dateKind.key === DateKindKey.Trigger && !isNil(yDefaultAxisId) && (
              <ReferenceLine
                x="Day 0"
                stroke="#8a94bd"
                yAxisId={yDefaultAxisId}
                label={
                  <Label
                    value={'Day 0'}
                    position="top"
                    style={{
                      fill: theme.colors.secondary800,
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  />
                }
              />
            )}

            {displayedHighlights.map((highlight, index) => {
              const yAxisId = getMeasureYAxisPositionByKey(
                highlight.relatedLine.measureKey,
              );
              const measureKey = highlight.relatedLine.measureKey;
              const displayedMeasure = displayedMeasures.find(
                (measure) => measure.key === measureKey,
              );
              if (!displayedMeasure) {
                return null;
              }
              const formattedValue = computeMeasureValue(
                highlight.value,
                displayedMeasure.format?.calculationUnit,
                displayedMeasure.format?.calculationValue,
              );

              return (
                <ReferenceDot
                  yAxisId={yAxisId}
                  label={CustomReferenceDotLabel}
                  className={`highlight-dot`}
                  key={`dot-${index}`}
                  isFront={true}
                  x={highlight.date}
                  y={formattedValue}
                  r={5}
                  fill="#ff625a"
                  stroke="white"
                  strokeWidth={1}
                  onClick={lockTooltipPositionHandler}
                  onMouseOver={(props) => {
                    if (!props.cx || !props.cy) {
                      return;
                    }
                    onLineDotMouseEnter(
                      {
                        x: props.cx,
                        y: props.cy,
                        date: highlight.date,
                        xAxisKey,
                      },
                      highlight.relatedLine,
                      highlight,
                    );
                  }}
                  onMouseLeave={onMouseLeave}
                />
              );
            })}
          </RechartsLineChart>
        </ResponsiveContainer>
      </ResponsiveChartWrapper>
      {tooltipProps && (
        <SingleChartDataTooltip
          analysisId={analysisId}
          handleCreateCohort={handleCreateCohortFromExpression}
          tooltipProps={tooltipProps}
          clearLockedTooltipProps={clearLockedTooltipProps}
          isTooltipLocked={isTooltipLocked}
        />
      )}
    </>
  );
});
